/**
 * #9163 W1/W2/W3a (contract agreed with helix-c9, 2026-10-01): typed choices under an answer.
 * `breakdown` offers the dimensions that split the figure (one may already be `applied`),
 * `clarification` replaces a free-text dead end, and `suggestion` offers the nearest members
 * after a zero-hit search. A click sends `{ token, id }` back and never text; the server
 * resolves it against the typed query it stored for `token` (stale or unknown: CHOICE_STALE).
 * Every label is server-authored; an amount is only ever a numericBindings placeholder.
 */
export const ASK_CHOICE_SET_KINDS = ['breakdown', 'clarification', 'suggestion'] as const;
export type AskChoiceSetKind = (typeof ASK_CHOICE_SET_KINDS)[number];
export const ASK_CHOICE_SET_MAX = 8;
/** A choice id or token longer than this is never minted, and never accepted on a click. */
export const ASK_CHOICE_TEXT_MAX = 512;
/** A clarification's choice counts: members of one grouped query, or governed measures (#9298). */
export const CLARIFY_MEMBER_CHOICES = { min: 2, max: 8 } as const;
export const CLARIFY_MEASURE_CHOICES = { min: 2, max: 5 } as const;
/** Why a set is offered again: `stale` re-offers the current chips after an earlier click. */
export const ASK_CHOICE_REOFFERS = ['stale'] as const;
export type AskChoiceReoffer = (typeof ASK_CHOICE_REOFFERS)[number];

export type AskChoice = Readonly<{
  /** Opaque and server-minted; echoed back verbatim. */
  id: string;
  label: string;
  /** Breakdown chips: how many members split the figure. */
  memberCount?: number;
  /** A numericBindings placeholder: the engine-bound amount, fenced like any figure. */
  figure?: string;
  /** The chip the server already ran; at most one per set. */
  applied?: true;
}>;

export type AskChoiceSet = Readonly<{
  kind: AskChoiceSetKind;
  prompt: string;
  choices: readonly AskChoice[];
  /** Binds the choices to the typed query they edit; opaque to the reader. */
  token: string;
  /** Set only when a click was stale and these are the current chips offered again. */
  reoffer?: AskChoiceReoffer;
}>;

/** The reply that resolves a choice: no question text travels with it. */
export type AskChoiceReply = Readonly<{ token: string; id: string }>;

const blank = (value: unknown): boolean => typeof value !== 'string' || value.trim() === '';

/** Every reason the set is not drawable as declared; empty means it is. */
export function askChoiceSetProblems(set: AskChoiceSet): string[] {
  const problems: string[] = [];
  if (!ASK_CHOICE_SET_KINDS.includes(set.kind)) problems.push('kind is not declared');
  if (blank(set.prompt)) problems.push('prompt is blank');
  if (blank(set.token)) problems.push('token is blank');
  if (set.reoffer !== undefined && !ASK_CHOICE_REOFFERS.includes(set.reoffer))
    problems.push('reoffer is not declared');
  const { choices } = set;
  if (choices.length < 1 || choices.length > ASK_CHOICE_SET_MAX)
    problems.push(`choices must number 1..${ASK_CHOICE_SET_MAX}`);
  if (new Set(choices.map((choice) => choice.id)).size !== choices.length)
    problems.push('choice ids repeat');
  if (choices.filter((choice) => choice.applied === true).length > 1)
    problems.push('more than one choice is applied');
  choices.forEach((choice, index) => {
    if (blank(choice.id)) problems.push(`choices[${index}].id is blank`);
    if (blank(choice.label)) problems.push(`choices[${index}].label is blank`);
    if (choice.memberCount !== undefined
      && (!Number.isInteger(choice.memberCount) || choice.memberCount < 0))
      problems.push(`choices[${index}].memberCount is not a count`);
    if (choice.figure !== undefined && blank(choice.figure))
      problems.push(`choices[${index}].figure is blank`);
  });
  return problems;
}
